import React, { useContext, useMemo, useState } from 'react';
import {
    Table,
    TableBody,
    TableContainer,
    TablePagination,
    TableRow,
    TableCell,
    Box,
    Chip,
    Stack,
} from '@mui/material';
import TaskMasterTableHead from './TaskMasterTableHead';
import { TaskMasterContext } from '../../context';
import moment from 'moment/moment';
import { Cancel, CheckCircle, EditNoteOutlined } from '@mui/icons-material';
import TableSkeleton from '../common/TableSkeleton';
import commonApi from '../../utils/api';
import { scrollToTop } from '../common/scrollToTop';

export default function TaskMasterTable() {
    const { firstLoading, allData, taskList, selected, page, setPage, rowsPerPage, setRowsPerPage, viewDetail, setIsLoading, onLoadApi } = useContext(TaskMasterContext);

    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('created_at');

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(event.target.value);
        setPage(0);
    };

    const sortedRows = useMemo(() => {
        return [...taskList].sort((a, b) => {
            if (order === 'asc') {
                return a[orderBy] > b[orderBy] ? 1 : -1;
            } else {
                return a[orderBy] < b[orderBy] ? 1 : -1;
            }
        });
    }, [taskList, orderBy, order]);

    const toggleStatus = (id, status) => {
        setIsLoading(true);
        commonApi.post('/users/task-master/status', { id: id, status: status })
            .then((response) => {
                scrollToTop();
                setIsLoading(false);
                if (response?.data?.s === 1) {
                    onLoadApi();
                }
            })
            .catch((error) => {
                scrollToTop();
                setIsLoading(false);
                console.error('Error toggling status:', error);
            });
    }

    const formatValue = (val) => {
        if (typeof val === 'object' && val !== null && val.type === 'Buffer' && Array.isArray(val.data)) {
            return String.fromCharCode.apply(null, val.data);
        }
        return val || '';
    };

    const stickyActionStyle = {
        position: 'sticky',
        right: 0,
        backgroundColor: '#fff',
        borderLeft: '1px solid #ddd',
        boxShadow: 'inset 4px 0 8px -4px rgba(0, 0, 0, 0.2)',
        zIndex: 3,
    };

    return (
        <div className='table-component'>
            {firstLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
                    <TablePagination
                        rowsPerPageOptions={[20, 50, 100, 150, 200, 250]}
                        component="div"
                        count={allData?.TotalRows || 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Box>
            ) : null}
            <TableContainer>
                <Table size="small">
                    <TaskMasterTableHead
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                    />
                    <TableBody>
                        {!firstLoading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ p: 0 }}>
                                    <TableSkeleton rows={10} columns={6} />
                                </TableCell>
                            </TableRow>
                        ) : sortedRows.length > 0 ?
                            sortedRows.map((row) => (
                                <TableRow hover tabIndex={-1} key={row.id}>
                                    <TableCell>{formatValue(row.id)}</TableCell>
                                    <TableCell>{formatValue(row.task_master) || "NA"}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Box sx={{
                                                width: 20,
                                                height: 20,
                                                backgroundColor: formatValue(row.color_code) || '#000',
                                                borderRadius: '50%'
                                            }} />
                                            <span>{formatValue(row.color_code) || "NA"}</span>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        {row.created_at ? moment(formatValue(row.created_at)).format("DD-MM-YYYY") : "NA"}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            variant="outlined"
                                            label={row?.status === 1 ? 'Active' : 'InActive'}
                                            color={row?.status === 1 ? 'success' : 'error'}
                                            size="small"
                                            icon={row?.status === 1 ? <CheckCircle fontSize="small" /> : <Cancel fontSize="small" />}
                                            onClick={() => toggleStatus(row.id, row?.status === 1 ? 0 : 1)}
                                        />
                                    </TableCell>
                                    <TableCell sx={stickyActionStyle}>
                                        <Chip
                                            variant="outlined"
                                            label="Edit"
                                            size="small"
                                            icon={<EditNoteOutlined fontSize="small" />}
                                            onClick={() => viewDetail(row.id)}
                                        />
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        No data found
                                    </TableCell>
                                </TableRow>
                            )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}
